import React from "react";

import type { BannerProps } from "@/ui/molecules/Banner";
import type { UserGroupIndicatorProps } from "@/ui/molecules/UserGroupIndicator";
import Banner from "@/ui/molecules/Banner";
import UserGroupIndicator from "@/ui/molecules/UserGroupIndicator";

type TResourceKey = "extraction-mapping" | "coa-remapping";

export type ViewConcurrentUsersParams = {
    useState: () => {
        concurrentUsers: {
            email: string;
        }[];
        showBanner: boolean;
    };
    useCases: () => {
        dismissBanner: () => void;
        startListeningForConcurrentUsers: (params: {
            extractionId: number;
            resourceKey: TResourceKey;
        }) => void;
        stopListeningForConcurrentUsers: () => void;
    };
};

export type ViewConcurrentUsersIndicatorProps = {
    sx?: UserGroupIndicatorProps["sx"];
};
export type ViewConcurrentUsersBannerProps = { sx?: BannerProps["sx"] };
export type HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmountProps<
    T extends TResourceKey,
> = {
    extractionId: number;
    resourceKey: T;
};

export const createViewConcurrentUsers = ({
    useState,
    useCases,
}: ViewConcurrentUsersParams) => {
    const HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount = <
        T extends TResourceKey,
    >(
        props: HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmountProps<T>,
    ) => {
        const { extractionId, resourceKey } = props;

        const {
            startListeningForConcurrentUsers,
            stopListeningForConcurrentUsers,
        } = useCases();

        React.useEffect(() => {
            startListeningForConcurrentUsers({
                extractionId,
                resourceKey,
            });

            return stopListeningForConcurrentUsers;
        }, [
            extractionId,
            resourceKey,
            startListeningForConcurrentUsers,
            stopListeningForConcurrentUsers,
        ]);

        return null;
    };

    const ViewConcurrentUsersIndicator: React.FC<
        ViewConcurrentUsersIndicatorProps
    > = props => {
        const { sx } = props;

        const { concurrentUsers } = useState();

        return (
            <UserGroupIndicator
                users={concurrentUsers}
                data-testid="ViewConcurrentUsersIndicator"
                sx={{
                    ...sx,
                }}
            />
        );
    };

    const ViewConcurrentUsersBanner: React.FC<
        ViewConcurrentUsersBannerProps
    > = props => {
        const { sx } = props;

        const { showBanner, concurrentUsers } = useState();

        const { dismissBanner } = useCases();

        return (
            showBanner &&
            concurrentUsers.length > 0 && (
                <Banner
                    dismiss={dismissBanner}
                    content="Someone else is looking at the same data. Your edits may be overridden."
                    icon={{
                        icon: "triangle-exclamation",
                        color: "var(--proda-black)",
                    }}
                    sx={{
                        backgroundColor: "var(--proda-yellow-20)",
                        ...sx,
                    }}
                />
            )
        );
    };

    return {
        ViewConcurrentUsersIndicator,
        ViewConcurrentUsersBanner,
        HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount,
    };
};
